import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Container, Nav, Navbar, Badge } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../../firebase';
import { doc, onSnapshot, collection, query, where, orderBy } from 'firebase/firestore';
import { createLogger } from '../../utils/logger';

const logger = createLogger('Header');
const EMPLOYEE_NOTIFICATION_RECENT_MS = 5 * 60 * 1000;

const getTimestampMillis = (timestamp) => {
    if (!timestamp) return null;
    if (typeof timestamp.toMillis === 'function') return timestamp.toMillis();
    if (typeof timestamp.toDate === 'function') return timestamp.toDate().getTime();
    if (timestamp instanceof Date) return timestamp.getTime();
    return null;
};

export default function Header() {
    const location = useLocation();
    const { currentUser, logout, userImage, userRole, employeeSupermarketId } = useAuth();
    const [loginUi, setLoginUi] = useState(<></>);
    const [cartUpdateCount, setCartUpdateCount] = useState(0);
    const [employeeCartReadyCount, setEmployeeCartReadyCount] = useState(0);
    const employeeCartSeenRef = useRef(new Set());
    const employeeNotificationInitializedRef = useRef(false);

    // Debug logging for admin role
    useEffect(() => {
        logger.debug(`[Header] User role changed: ${userRole}, currentUser: ${!!currentUser}`);
    }, [userRole, currentUser]);

    const handleLogout = useCallback(async () => {
        try {
            await logout();
        } catch (e) {
            logger.error("Logout error", e);
        }
    }, [logout]);

    useEffect(() => {
        if (!currentUser) {
          setLoginUi(
            <Nav.Link as={Link} to={`/login?path=${  location.pathname}`} title='login' className="p-0">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" className="bi bi-person-x align-middle" viewBox="0 0 16 16">
                <path d="M11 5a3 3 0 1 1-6 0 3 3 0 0 1 6 0M8 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4m.256 7a4.5 4.5 0 0 1-.229-1.004H3c.001-.246.154-.986.832-1.664C4.484 10.68 5.711 10 8 10q.39 0 .74.025c.226-.341.496-.65.804-.918Q8.844 9.002 8 9c-5 0-6 3-6 4s1 1 1 1z"/>
                <path d="M12.5 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7m-.646-4.854.646.647.646-.647a.5.5 0 0 1 .708.708l-.647.646.647.646a.5.5 0 0 1-.708.708l-.646-.647-.646.647a.5.5 0 0 1-.708-.708l.647-.646-.647-.646a.5.5 0 0 1 .708-.708"/>
              </svg>
            </Nav.Link>
          );
        } else {
          const userName = currentUser.displayName || currentUser.email || 'U';
          const userInitial = userName.charAt(0).toUpperCase();
          setLoginUi(
            <button
              type="button"
              onClick={handleLogout}
              title="logout"
              className="navbar-logout-btn d-flex align-items-center gap-2 btn btn-link p-0 border-0 text-decoration-none"
            >
              <div className="pic-frame">
                {userImage ? (
                  <img src={userImage} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span>{userInitial}</span>
                )}
              </div>
              <span className="menu-text d-sm-none">Logout</span>
            </button>
          );
        }
      }, [userImage, currentUser, logout, location.pathname, handleLogout]);

    useEffect(() => {
        if (currentUser) {
            const cartRef = doc(db, 'Carts', currentUser.uid);
            const unsubscribe = onSnapshot(cartRef, (docSnap) => {
                let unseenCount = 0;
                if (docSnap.exists()) {
                    const cartData = docSnap.data();
                    for (const supermarketId in cartData) {
                        if (supermarketId === 'lastGlobalUpdate') continue;
                        const supermarketInstances = cartData[supermarketId];
                        if (typeof supermarketInstances === 'object' && supermarketInstances !== null) {
                            for (const instanceId in supermarketInstances) {
                                if (supermarketInstances[instanceId] && supermarketInstances[instanceId].hasUnseenUpdate === true) {
                                    unseenCount++;
                                }
                            }
                        }
                    }
                }
                setCartUpdateCount(unseenCount);
            }, (error) => {
                // Handle permission errors gracefully
                if (error.code === 'permission-denied') {
                    logger.warn('[Header] Cart notification permission denied - user may not have access');
                    setCartUpdateCount(0);
                } else {
                    logger.error('[Header] Cart notification error:', error);
                }
            });
            return () => unsubscribe();
        } else {
            setCartUpdateCount(0);
        }
    }, [currentUser]);

    // Employee: Notification for new carts ready to be packed
    useEffect(() => {
        if (currentUser && (userRole === 'Employee' || userRole === 'Admin')) {
            if (userRole === 'Employee' && !employeeSupermarketId) {
                setEmployeeCartReadyCount(0);
                return;
            }
            
            // For employees, listen to their assigned supermarket. For admins, listen to all supermarkets
            let transactionsQuery;
            if (userRole === 'Employee') {
                transactionsQuery = query(
                    collection(db, 'Transactions'),
                    where('status', 'in', ['payment_successful', 'awaiting_packing']),
                    where('supermarketId', '==', employeeSupermarketId),
                    orderBy('createdAt', 'desc')
                );
            } else {
                // Admin: listen to all supermarkets
                transactionsQuery = query(
                    collection(db, 'Transactions'),
                    where('status', 'in', ['payment_successful', 'awaiting_packing']),
                    orderBy('createdAt', 'desc')
                );
            }
            
            const unsubscribe = onSnapshot(transactionsQuery, snapshot => {
                const seenIds = employeeCartSeenRef.current;

                // First snapshot: keep fresh checkout notifications visible after reloads,
                // while marking older existing orders as seen so stale badges do not appear.
                if (!employeeNotificationInitializedRef.current) {
                    const currentTime = Date.now();
                    let initialUnseen = 0;

                    snapshot.forEach(doc => {
                        const transactionData = doc.data();
                        const transactionTime = getTimestampMillis(transactionData.createdAt) || currentTime;

                        if (!seenIds.has(doc.id) && currentTime - transactionTime < EMPLOYEE_NOTIFICATION_RECENT_MS) {
                            initialUnseen++;
                        }
                        seenIds.add(doc.id);
                    });

                    employeeNotificationInitializedRef.current = true;
                    setEmployeeCartReadyCount(initialUnseen);
                    return;
                }

                let newUnseen = 0;
                snapshot.docChanges().forEach(change => {
                    if (change.type === 'added' && !seenIds.has(change.doc.id)) {
                        seenIds.add(change.doc.id);
                        newUnseen++;
                    }
                });

                if (newUnseen > 0) {
                    setEmployeeCartReadyCount(prev => prev + newUnseen);
                }
            }, (error) => {
                // Handle permission errors gracefully
                if (error.code === 'permission-denied') {
                    logger.warn('[Header] Employee notification permission denied - user may not have access');
                    setEmployeeCartReadyCount(0);
                } else {
                    logger.error('[Header] Employee notification error:', error);
                }
            });
            return () => {
                unsubscribe();
                employeeNotificationInitializedRef.current = false;
            };
        } else {
            setEmployeeCartReadyCount(0);
            employeeCartSeenRef.current = new Set();
            employeeNotificationInitializedRef.current = false;
        }
    }, [currentUser, userRole, employeeSupermarketId]);

    // Clear employee notifications when visiting employee dashboard
    useEffect(() => {
        if (currentUser && (userRole === 'Employee' || userRole === 'Admin') && location.pathname === '/employee/dashboard') {
            setEmployeeCartReadyCount(0);
        }
    }, [currentUser, userRole, location.pathname]);

  const isProductsActive = location.pathname.startsWith("/products") ||
    location.pathname.startsWith("/product/") ||
    location.pathname.startsWith("/supermarkets");

  const isCartsActive = location.pathname.startsWith("/carts") ||
    location.pathname.startsWith("/checkout") ||
    location.pathname.startsWith("/payment");

  return (
    <div>
      <Navbar collapseOnSelect expand="sm" fixed="top" className='navbar-custom mh-10 px-3 bg-main'> 
        <Container fluid>
          <Navbar.Brand as={Link} to="/" className="d-flex align-items-center gap-2 text-decoration-none">
            <img height={'33px'} src='/logo192.png' alt='Pack My Cart logo' />
            <span className="navbar-brand-text">Pack My Cart</span>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="navbarScroll" />
          <Navbar.Collapse id="navbarScroll">
            <Nav className="me-auto">
              <Nav.Link as={Link} to="/" className={`m-option text-decoration-none nav-link text-center ${location.pathname === "/" ? "active" : ""}`}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width={location.pathname === "/" ? "30" : "25"} height={location.pathname === "/" ? "30" : "25"} className="bi m-icon" viewBox="0 0 16 16">
                  {location.pathname === "/" ? <path d="M6.5 14.5v-3.505c0-.245.25-.495.5-.495h2c.25 0 .5.25.5.5v3.5a.5.5 0 0 0 .5.5h4a.5.5 0 0 0 .5-.5v-7a.5.5 0 0 0-.146-.354L13 5.793V2.5a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v1.293L8.354 1.146a.5.5 0 0 0-.708 0l-6 6A.5.5 0 0 0 1.5 7.5v7a.5.5 0 0 0 .5.5h4a.5.5 0 0 0 .5-.5Z"/> : <path d="M8.354 1.146a.5.5 0 0 0-.708 0l-6 6A.5.5 0 0 0 1.5 7.5v7a.5.5 0 0 0 .5.5h4.5a.5.5 0 0 0 .5-.5v-4h2v4a.5.5 0 0 0 .5.5H14a.5.5 0 0 0 .5-.5v-7a.5.5 0 0 0-.146-.354L13 5.793V2.5a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v1.293L8.354 1.146ZM2.5 14V7.707l5.5-5.5 5.5 5.5V14H10v-4a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 0-.5.5v4H2.5Z"/>}
                </svg>
                <span className='menu-text'>Home</span>
              </Nav.Link>
              <Nav.Link as={Link} to="/products" className={`m-option text-decoration-none nav-link text-center ${isProductsActive ? "active" : ""}`}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width={isProductsActive ? "30" : "25"} height={isProductsActive ? "30" : "25"} className="bi m-icon" viewBox="0 0 16 16">
                  <path d="M2.97 1.35A1 1 0 0 1 3.73 1h8.54a1 1 0 0 1 .76.35l2.609 3.044A1.5 1.5 0 0 1 16 5.37v.255a2.375 2.375 0 0 1-4.25 1.458A2.371 2.371 0 0 1 9.875 8 2.37 2.37 0 0 1 8 7.083 2.37 2.37 0 0 1 6.125 8a2.37 2.37 0 0 1-1.875-.917A2.375 2.375 0 0 1 0 5.625V5.37a1.5 1.5 0 0 1 .361-.976l2.61-3.045zm1.78 4.275a1.375 1.375 0 0 0 2.75 0 .5.5 0 0 1 1 0 1.375 1.375 0 0 0 2.75 0 .5.5 0 0 1 1 0 1.375 1.375 0 1 0 2.75 0V5.37a.5.5 0 0 0-.12-.325L12.27 2H3.73L1.12 5.045A.5.5 0 0 0 1 5.37v.255a1.375 1.375 0 0 0 2.75 0 .5.5 0 0 1 1 0zM1.5 8.5A.5.5 0 0 1 2 9v6h1v-5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v5h6V9a.5.5 0 0 1 1 0v6a.5.5 0 0 1-.5.5H.5a.5.5 0 0 1-.5-.5V9a.5.5 0 0 1 .5-.5zM4 15h3v-5H4v5z"/>
                </svg>
                <span className='menu-text'>Products</span>
              </Nav.Link>
              {currentUser && (userRole === 'Admin' || userRole === 'Employee') && (
                <Nav.Link as={Link} to="/inventory" className={`m-option text-decoration-none nav-link text-center ${location.pathname.startsWith("/inventory") ? "active" : ""}`}>
                  <svg fill="currentColor" width={location.pathname.startsWith("/inventory") ? "30" : "25"}  height={location.pathname.startsWith("/inventory") ? "30" : "25"} viewBox="0 0 16 16"><path d="M8 1a2.5 2.5 0 0 1 2.5 2.5V4h-5v-.5A2.5 2.5 0 0 1 8 1zm3.5 3v-.5a3.5 3.5 0 1 0-7 0V4H1v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4h-3.5zM2 5h12v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V5z"/></svg>
                  <span className='menu-text'>Inventory</span>
                </Nav.Link>
              )}
              {currentUser && (
                <Nav.Link as={Link} to="/shopping-lists" className={`m-option text-decoration-none nav-link text-center ${location.pathname.startsWith("/shopping-lists") ? "active" : ""}`}>
                  <svg fill="currentColor" width={location.pathname.startsWith("/shopping-lists") ? "30" : "25"} height={location.pathname.startsWith("/shopping-lists") ? "30" : "25"} viewBox="0 0 16 16"><path fillRule="evenodd" d="M5 11.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zM3.854 2.146a.5.5 0 0 1 0 .708l-1.5 1.5a.5.5 0 0 1-.708 0l-.5-.5a.5.5 0 1 1 .708-.708L2 3.293l1.146-1.147a.5.5 0 0 1 .708 0zm0 4a.5.5 0 0 1 0 .708l-1.5 1.5a.5.5 0 0 1-.708 0l-.5-.5a.5.5 0 1 1 .708-.708L2 7.293l1.146-1.147a.5.5 0 0 1 .708 0zm0 4a.5.5 0 0 1 0 .708l-1.5 1.5a.5.5 0 0 1-.708 0l-.5-.5a.5.5 0 0 1 .708-.708L2 11.293l1.146-1.147a.5.5 0 0 1 .708 0z"/></svg>
                  <span className='menu-text'>Lists</span>
                </Nav.Link>
              )}
              <Nav.Link as={Link} to="/carts" className={`m-option text-decoration-none nav-link text-center ${isCartsActive ? "active" : ""}`} style={{position: 'relative'}}>
                <svg fill="currentColor" width={isCartsActive ? "30" : "25"}  height={isCartsActive ? "30" : "25"} viewBox="0 0 16 16"><path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .491.592l-1.5 8A.5.5 0 0 1 13 12H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5zM3.102 4l1.313 7h8.17l1.313-7H3.102zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm-7 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm7 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/></svg>
                <span className='menu-text'>Carts</span>
                {cartUpdateCount > 0 && 
                  <Badge pill bg="danger" style={{ position: 'absolute', top: '2px', right: '2px', fontSize: '0.6em' }}>
                    {cartUpdateCount}
                  </Badge>
                }
              </Nav.Link>
              {currentUser && (userRole === 'Admin' || userRole === 'Employee') && (
                <Nav.Link as={Link} to="/employee/dashboard" className={`m-option text-decoration-none nav-link text-center ${location.pathname.startsWith("/employee") ? "active" : ""}`} style={{position: 'relative'}}>
                  <svg xmlns="http://www.w3.org/2000/svg" width={location.pathname.startsWith("/employee") ? "30" : "25"} height={location.pathname.startsWith("/employee") ? "30" : "25"} fill="currentColor" className="bi bi-briefcase" viewBox="0 0 16 16">
                    <path d="M6.5 1A1.5 1.5 0 0 0 5 2.5V3H1.5A1.5 1.5 0 0 0 0 4.5v8A1.5 1.5 0 0 0 1.5 14h13a1.5 1.5 0 0 0 1.5-1.5v-8A1.5 1.5 0 0 0 14.5 3H11v-.5A1.5 1.5 0 0 0 9.5 1h-3zm0 1h3a.5.5 0 0 1 .5.5V3H6v-.5a.5.5 0 0 1 .5-.5zM1.5 4h13a.5.5 0 0 1 .5.5v8a.5.5 0 0 1-.5.5h-13a.5.5 0 0 1-.5-.5v-8a.5.5 0 0 1 .5-.5z"/>
                  </svg>
                  <span className='menu-text'>Employee Hub</span>
                  {(userRole === 'Employee' || userRole === 'Admin') && employeeCartReadyCount > 0 && (
                    <Badge pill bg="danger" style={{ position: 'absolute', top: '2px', right: '2px', fontSize: '0.6em' }}>
                      {employeeCartReadyCount}
                    </Badge>
                  )}
                </Nav.Link>
              )}
              {currentUser && userRole === 'Admin' && (
                <Nav.Link as={Link} to="/admin" className={`m-option text-decoration-none nav-link text-center ${location.pathname.startsWith("/admin") ? "active" : ""}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width={location.pathname.startsWith("/admin") ? "30" : "25"} height={location.pathname.startsWith("/admin") ? "30" : "25"} className="bi bi-person-badge" viewBox="0 0 16 16">
                    <path d="M6.5 2a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1zM11 8a3 3 0 1 1-6 0 3 3 0 0 1 6 0z"/>
                    <path d="M4.5 0A2.5 2.5 0 0 0 2 2.5V14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2.5A2.5 2.5 0 0 0 11.5 0h-7zM3 2.5A1.5 1.5 0 0 1 4.5 1h7A1.5 1.5 0 0 1 13 2.5v10.795a4.2 4.2 0 0 0-.776-.492C11.392 12.387 10.063 12 8 12s-3.392.387-4.224.803a4.2 4.2 0 0 0-.776.492V2.5z"/>
                  </svg>
                  <span className='menu-text'>Admin</span>
                </Nav.Link>
              )}
            </Nav>
            <Nav className="ms-auto align-items-center">
              {loginUi}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>
    </div>
  );
}
